<script setup lang="ts">
/**
 * BaseButton — components.css 의 .btn 계열 원자.
 *  variant: gold(.btn-gold) · line(.btn-line) · linelight(.btn-linelight) · default(.btn-navy)
 *  size:    lg(.btn.lg) · md(기본) · sm(목록·인라인 보조 액션)
 * 색은 전부 토큰(Tailwind 매핑색) 경유 — hex 직접 박지 않음.
 */
import { computed } from 'vue';
import type { VariantProps } from 'class-variance-authority';
import { cn } from '#/lib/utils';
// CVA 정의는 buttonVariants.ts 가 SSoT(<script setup> 은 값 export 불가).
// RouterLink/<a> 를 버튼 스타일로 쓸 땐 소비처가 buttonVariants 를 직접 import 한다.
import { buttonVariants, type ButtonVariant } from './buttonVariants';

type ButtonVariantProps = VariantProps<typeof buttonVariants>;

interface Props {
  /** 시각 변형 — 프로토타입 .btn-* 매핑 */
  variant?: ButtonVariant;
  /** 크기 — lg(.btn.lg) / md(기본) */
  size?: NonNullable<ButtonVariantProps['size']>;
  /** width:100% (.btn.block) */
  block?: boolean;
  /** 네이티브 button type */
  type?: 'button' | 'submit' | 'reset';
  /** 비활성 — 시각(opacity .5) + 동작 차단 */
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'accent',
  size: 'md',
  block: false,
  type: 'button',
  disabled: false,
});

defineEmits<{
  click: [event: MouseEvent];
}>();

const classes = computed(() =>
  cn(buttonVariants({ variant: props.variant, size: props.size, block: props.block })),
);
</script>

<template>
  <button
    :class="classes"
    :type="type"
    :disabled="disabled"
    :aria-disabled="disabled || undefined"
    @click="$emit('click', $event)"
  >
    <slot />
  </button>
</template>

<style scoped>
/* .btn svg{width:17px;height:17px;flex:0 0 auto} — 슬롯으로 들어온 아이콘 크기 고정 */
.fo-btn :deep(svg) {
  width: 17px;
  height: 17px;
  flex: 0 0 auto;
}
</style>
